import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api, { formatMoney, formatNumber, formatPct } from "../lib/api";
import { InfoD } from "./InfoD";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";
import { col } from "../lib/exportCols";

export function RankingLabsDParado({ studyId }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/inventory/labs-d-parado", { params: { study_id: studyId } }).then((r) => setData(r.data)).catch(() => setData({ labs: [] }));
  }, [studyId]);
  const sort = useSortable(data?.labs || [], "stock_eur", "desc");

  if (!data) return <div className="card p-6 mb-8 text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" />Cargando ranking de laboratorios con stock D…</div>;
  if (!data.labs.length) return null;
  const open = (lab) => navigate(`/studies/${data.study_id}?tab=laboratorios&lab=${encodeURIComponent(lab)}`);
  const top = data.labs.slice(0, 12);

  return (
    <div className="card p-6 mb-8" data-testid="labs-d-ranking">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Stock D parado · {data.study_name}</div>
          <h3 className="font-display text-xl font-bold mt-1">Ranking de laboratorios con más stock D parado</h3>
        </div>
        <div className="text-right">
          <div className="kpi-label flex items-center gap-1 justify-end">Total clase D con stock <InfoD term="clase" /></div>
          <div className="font-display text-xl font-extrabold text-red-600" data-testid="labs-d-total">{formatMoney(data.total_eur)}</div>
          <div className="text-[11px] text-slate-500">{formatNumber(data.total_labs)} laboratorios</div>
          <ExportButtons className="mt-2" filename="ranking-labs-stock-d" title="Laboratorios con más stock D parado" subtitle={data.study_name}
                         sheetName="Labs D" testIdPrefix="labs-d-export" rows={sort.sortedRows}
                         columns={[col.text("Laboratorio", "laboratorio"), col.eur("Valor D", "stock_eur"), col.pct("% del D", "pct"),
                                   col.num("Artículos", "refs"), col.num("> 1 año", "refs_1y"), col.num("Uds.", "stock_uds")]} />
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ResponsiveContainer width="100%" height={Math.max(260, top.length * 28)}>
          <BarChart data={top} layout="vertical" margin={{ left: 10, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <YAxis type="category" dataKey="laboratorio" width={130} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => formatMoney(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
            <Bar dataKey="stock_eur" name="Stock D (€)" fill="#DC2626" radius={[0, 4, 4, 0]} style={{ cursor: "pointer" }}
                 onClick={(d) => d?.laboratorio && open(d.laboratorio)} />
          </BarChart>
        </ResponsiveContainer>
        <div className="overflow-auto max-h-[420px]">
          <table className="dense" data-testid="labs-d-table">
            <thead><tr><th>#</th>
              <th><SortHeader label="Laboratorio" k="laboratorio" sort={sort} defaultDir="asc" /></th>
              <th className="num"><SortHeader label="Valor D" k="stock_eur" sort={sort} num /></th>
              <th className="num"><SortHeader label="% del D" k="pct" sort={sort} num /></th>
              <th className="num"><SortHeader label="Artículos" k="refs" sort={sort} num /></th>
              <th className="num"><SortHeader label="> 1 año" k="refs_1y" sort={sort} num /></th>
              <th className="num"><SortHeader label="Uds." k="stock_uds" sort={sort} num /></th></tr></thead>
            <tbody>
              {sort.sortedRows.map((l, i) => (
                <tr key={l.laboratorio} className="cursor-pointer hover:bg-red-50" onClick={() => open(l.laboratorio)} data-testid={`labs-d-row-${i}`}>
                  <td className="font-mono text-slate-400">{i + 1}</td>
                  <td className="font-semibold">{l.laboratorio}</td>
                  <td className="num font-bold text-red-600">{formatMoney(l.stock_eur)}</td>
                  <td className="num text-slate-600">{formatPct(l.pct)}</td>
                  <td className="num">{formatNumber(l.refs)}</td>
                  <td className="num">{formatNumber(l.refs_1y)}</td>
                  <td className="num">{formatNumber(l.stock_uds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
